export type NestClientStatus = "connecting" | "connected" | "reconnecting" | "unauthorized" | "disconnected"
export type NestRpcLogPhase = "status" | "send" | "ok" | "error" | "notification"

export interface NestRpcMethodStats {
    count: number
    windowCount: number
    p50Ms: number
    p95Ms: number
    maxMs: number
}

export interface NestRpcLogEvent {
    at: string
    phase: NestRpcLogPhase
    method?: string
    requestId?: number
    durationMs?: number
    status?: NestClientStatus | "ok" | "error"
    errorCode?: string
    cursor?: string
    context?: Record<string, string>
    pendingCount?: number
    socketState?: string
    methodStats?: NestRpcMethodStats
    responseChars?: number
    responseSize?: string
}

interface NestRpcLogEntry extends NestRpcLogEvent {
    atMs: number
}

interface NestRpcLogDebugApi {
    get(): NestRpcLogEvent[]
    clear(): void
    trace(enabled?: boolean): boolean
}

declare global {
    interface Window {
        __wildlarkNestRpcLog?: NestRpcLogDebugApi
    }
}

const RPC_LOG_WINDOW_MS = 5 * 60_000
const RPC_LOG_MAX_EVENTS = 2_000
const RPC_METHOD_STATS_WINDOW = 128
const rpcLogEntries: NestRpcLogEntry[] = []
const rpcMethodStats = new Map<string, { count: number; durationsMs: number[] }>()

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function safeRpcLogContext(params: unknown): Record<string, string> {
    if (!isRecord(params)) return {}
    const context: Record<string, string> = {}
    for (const key of ["workspaceId", "sessionId", "turnId", "runtimeId", "processId", "terminalId"]) {
        const value = params[key]
        if (typeof value === "string" && value) context[key] = value
    }
    return context
}

export function isNestRpcTraceEnabled(): boolean {
    if (typeof localStorage === "undefined") return false
    return localStorage.getItem("wildlark:nest:traceRpc") === "1"
}

export function socketStateName(readyState: number | undefined): string {
    switch (readyState) {
        case 0:
            return "connecting"
        case 1:
            return "open"
        case 2:
            return "closing"
        case 3:
            return "closed"
        default:
            return "none"
    }
}

function trimNestRpcLog(nowMs = Date.now()): void {
    const cutoff = nowMs - RPC_LOG_WINDOW_MS
    while (rpcLogEntries.length > 0 && rpcLogEntries[0]?.atMs !== undefined && rpcLogEntries[0].atMs < cutoff) {
        rpcLogEntries.shift()
    }
    while (rpcLogEntries.length > RPC_LOG_MAX_EVENTS) {
        rpcLogEntries.shift()
    }
}

function percentile(sorted: number[], value: number): number {
    if (sorted.length === 0) return 0
    const index = Math.min(sorted.length - 1, Math.ceil((value / 100) * sorted.length) - 1)
    return sorted[index] ?? 0
}

export function recordNestRpcMethodDuration(method: string, durationMs: number): NestRpcMethodStats {
    const state = rpcMethodStats.get(method) ?? { count: 0, durationsMs: [] }
    state.count += 1
    state.durationsMs.push(durationMs)
    if (state.durationsMs.length > RPC_METHOD_STATS_WINDOW) {
        state.durationsMs.splice(0, state.durationsMs.length - RPC_METHOD_STATS_WINDOW)
    }
    rpcMethodStats.set(method, state)

    const sorted = [...state.durationsMs].sort((a, b) => a - b)
    return {
        count: state.count,
        windowCount: state.durationsMs.length,
        p50Ms: percentile(sorted, 50),
        p95Ms: percentile(sorted, 95),
        maxMs: sorted[sorted.length - 1] ?? 0,
    }
}

function mirrorNestRpcLog(event: NestRpcLogEvent): void {
    if (typeof window === "undefined") return
    try {
        window.wildlarkAPI?.nest?.logRpcEvent?.(event)
    } catch {
        // Logging must never break the websocket path.
    }
}

export function recordNestRpcLog(event: Omit<NestRpcLogEvent, "at">): void {
    const nowMs = Date.now()
    const entry: NestRpcLogEntry = {
        at: new Date(nowMs).toISOString(),
        atMs: nowMs,
        ...event,
    }
    rpcLogEntries.push(entry)
    trimNestRpcLog(nowMs)
    const { atMs: _atMs, ...externalEvent } = entry
    mirrorNestRpcLog(externalEvent)
}

export function getNestRpcLog(): NestRpcLogEvent[] {
    trimNestRpcLog()
    return rpcLogEntries.map(({ atMs: _atMs, ...event }) => event)
}

export function clearNestRpcLog(): void {
    rpcLogEntries.length = 0
    rpcMethodStats.clear()
}

export function installNestRpcLogDebugApi(): void {
    if (typeof window === "undefined") return
    window.__wildlarkNestRpcLog = {
        get: getNestRpcLog,
        clear: clearNestRpcLog,
        trace: (enabled = true) => {
            if (typeof localStorage !== "undefined") {
                if (enabled) localStorage.setItem("wildlark:nest:traceRpc", "1")
                else localStorage.removeItem("wildlark:nest:traceRpc")
            }
            return isNestRpcTraceEnabled()
        },
    }
}
